<script setup lang="ts">
import { ref } from 'vue';
import { onClickOutside } from '@vueuse/core';

import {
  useCoreStore,
  useRouterStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';

import {
  AppRawButton,
  AppRouterLink,
  AppStars,
  AppSwiperGallery,
} from '@libs/ui';

import type { HotelMapCardData } from '../models';

interface HotelMapCardProps {
  data: HotelMapCardData;
}

const props = defineProps<HotelMapCardProps>();

interface HotelMapCardEmits {
  (eventName: 'click'): void;
  (eventName: 'hide'): void;
}

const emit = defineEmits<HotelMapCardEmits>();

const coreStore = useCoreStore();
const routerStore = useRouterStore();
const statsigEventsStore = useStatsigEventsStore();

const hotelMapCard = ref<HTMLDivElement | undefined>(undefined);

onClickOutside(hotelMapCard, () => emit('hide'));

function onNavigationTriggered(): void {
  statsigEventsStore.logHotelSelectionMapCardClicked();
}
</script>

<template>
  <AppRawButton
    ref="hotelMapCard"
    class="hotel-map-card"
    @click="emit('click')"
  >
    <div class="image-wrapper">
      <AppSwiperGallery
        :image-urls="props.data.imageUrls"
        :show-nav-buttons="true"
        :display-gallery-dots="false"
        :allow-full-gallery="false"
        :error-image-handler="
          (src) =>
            coreStore.handleImageLoadError({
              src: src,
              source: 'hotel-selection-map-card',
            })
        "
        slides-amount-position="right"
        slides-amount-size="s"
        :left-arrow-aria-label="$t('a11y.previous_image')"
        :right-arrow-aria-label="$t('a11y.next_image')"
        :full-gallery-aria-label="$t('a11y.open_image_gallery')"
      />
    </div>
    <div class="content">
      <AppRouterLink
        :link="
          routerStore.getHotelDetailsUrlInHotelSelection(
            { ingredientIndex: props.data.ingredientIndex },
            props.data.hotelCode
          )
        "
        font="font-label-s-bold"
        color="color-neutral-70"
        @navigation-triggered="onNavigationTriggered"
      >
        {{ props.data.hotelName }}
      </AppRouterLink>
      <div class="stars-rating">
        <AppStars :amount="props.data.starsRating" size="18" />
      </div>
    </div>
  </AppRawButton>
</template>

<style scoped lang="scss">
.hotel-map-card {
  width: 256px;
  height: 198px;

  box-shadow: var(--shadow-elevation-200);

  display: flex;
  flex-flow: column;

  .image-wrapper {
    flex-grow: 1;
    overflow: hidden;
    width: 100%;
    border-top-left-radius: var(--border-radius-12);
    border-top-right-radius: var(--border-radius-12);
    background-color: var(--color-white);
  }

  .content {
    padding: var(--spacing-12);
    background: var(--color-white);
    border-bottom-left-radius: var(--border-radius-12);
    border-bottom-right-radius: var(--border-radius-12);
    transition: background var(--transition-short);

    display: flex;
    flex-flow: column;
    gap: var(--spacing-8);
  }

  @include on-hover {
    cursor: pointer;

    .image-wrapper,
    .content {
      background: var(--color-neutral-30);
    }
  }
}
</style>
